import { unByKey } from 'ol/Observable';
import { Geometry } from 'ol/geom';
import { getUid } from 'ol';
import { getCenter, getIntersection, scaleFromCenter } from 'ol/extent';
import clamp from 'lodash-es/clamp';
import { METERS_PER_UNIT } from 'ol/proj/Units';

import { ViewProperty } from 'cadenza/map/view-property';
import { currentWindowMediaSmUp, supportsTouch } from 'cadenza/utils/media';
import { MapProperty } from 'cadenza/map/map-property';
import { context } from 'cadenza/map/context';

const ANIMATION_DURATION = 200;
export const DOTS_PER_INCH = 96;
export const INCHES_PER_METER = 39.3701;
// DEFAULT_SCALE is usable for each srs even with non-metric units as resolutionForScale scales
// that value so it fits independent of the srs' unit. Be careful though, this is not meant to be
// used for high precision as that scaling factor is only an approximation.
const DEFAULT_SCALE = 10000;

export const DEFAULT_ZOOM_OUT_FACTOR = 1.2;

export function animateDuringMapMove (map, listener) {
  const listeners = [
    map.on('movestart', () => {
      listeners.push(map.on('postcompose', () => { listener(); }));
    }),
    map.on('moveend', () => {
      while (listeners.length > 2) {
        unByKey(listeners.pop());
      }
      listener();
    })
  ];
  return listeners;
}

/**
 * Centers map view to given extent and zooms to the resolution of that extent. It does not zoom
 * below default resolution. (Default resolution is calculated from default scale which is 1:10000)
 *
 * @param {import('ol/Map').Map} map - map
 * @param {(import('ol/geom/Geometry').Geometry|import('ol/extent').Extent)} geometryOrExtent - geometry or extent
 * @param {object} [options] - additional options
 * @param {number} [options.maxZoomScale = 10000] - maximum scale to zoom, Default value is 10000
 * @param {number[]} [options.padding] - Padding (in pixels) to be cleared inside the view. Values in the array are top, right, bottom and left. If not given, no padding will be used. This padding does not override the view.padding, but will be added to it.
 * @return {Promise} The promise is resolved when the animation is finished.
 */
export function fitMapView (map, geometryOrExtent, { maxZoomScale = DEFAULT_SCALE, padding } = {}) {
  const view = map.getView();

  const currentScale = context(map).mapScale;

  const extent = geometryOrExtent instanceof Geometry
    ? geometryOrExtent.getExtent()
    : geometryOrExtent;
  // intersect with max extent to ensure that when a given (layer) extent is way bigger than
  // the configured maxExtent (areaOfInterest) to not zoom to the mostly random center. That
  // happens a lot for WMS layers which usually have a big content extent and are used as
  // background layers
  const extentWithinMaxExtent = intersectWithMaxExtent(view, extent);

  const center = getCenter(extentWithinMaxExtent);
  const resolution = getResolutionForExtent(view, extentWithinMaxExtent, Math.min(maxZoomScale, currentScale), padding);
  return new Promise(resolve => {
    view.animate({ center, resolution, duration: ANIMATION_DURATION }, resolve);
  });
}

function intersectWithMaxExtent (view, extent) {
  const maxExtent = view.get(ViewProperty.MAX_EXTENT);
  if (maxExtent) {
    return getIntersection(maxExtent, extent);
  }
  return extent;
}

function getResolutionForExtent (view, extent, maxZoomScale, padding) {
  const [ width, height ] = view.getViewportSizeMinusPadding();
  const [ padTop, padRight, padBottom, padLeft ] = padding || [ 0, 0, 0, 0 ];

  let resolution = view.getResolutionForExtent(extent, [
    width - padLeft - padRight,
    height - padTop - padBottom
  ]);

  resolution = Math.max(resolution, resolutionForScale(view, maxZoomScale));

  // ConstrainResolution helps to ensure that the resolution matches one of the 25 predefined zoom
  // levels (see DEFAULT_NUMBER_OF_ZOOM_LEVELS). It also ensures that the resolution can not get above or below the
  // minimum and maximum defined resolutions. The direction=1 parameter ensures that it does not
  // search for the nearest(direction=0) resolution but always zooms more out to ensure the given
  // extent is always visible.
  return view.getConstrainedResolution(resolution, 1);
}

/**
 * Centers map view to given extent +- the zoomFactor and zooms to the resolution of that extent.
 * It does not zoom below default resolution. (Default resolution is calculated from default scale which is 1:10000)
 *
 * @param {import('ol/Map').Map} map
 * @param {import('ol/extent').Extent} extent
 * @param {object} [options] - additional options
 * @param {number} [options.maxZoomScale = 10000] - maximum scale to zoom, Default value is 10000
 * @param {number[]} [options.padding] - Padding (in pixels) to be cleared inside the view. Values in the array are top, right, bottom and left. If not given, no padding will be used. This padding does not override the view.padding, but will be added to it.
 * @param {number} [options.zoomFactor = 1.2] - zoom factor to multiply the extent with. A value of 1.2 means 20% larger extent.
 * @return {Promise} The promise is resolved when the animation is finished.
 */
export function fitMapViewToScaledExtent (map, extent, { maxZoomScale, padding, zoomFactor = DEFAULT_ZOOM_OUT_FACTOR } = {}) {
  const scaledBoundingBox = scaleExtent(extent, zoomFactor);
  return fitMapView(map, scaledBoundingBox, { maxZoomScale, padding });
}

/**
 * Centers a map on a given coordinate with animation.
 *
 * @param {import('ol/Map').Map} map
 * @param {import('ol/coordinate').Coordinate} center
 */
export function centerMapView (map, center) {
  map.getView().animate({ center }, ANIMATION_DURATION);
}

/**
 * Scales the passed extent using the factor
 *
 * @param extent - extent
 * @param {number} zoomFactor - zoom factor to multiply the extent with.
 * @return {object} the new scaled extent
 */
export function scaleExtent (extent, zoomFactor) {
  const extentCopy = [ ...extent ];
  scaleFromCenter(extentCopy, zoomFactor);
  return extentCopy;
}

/**
 * Optimized alternative for OL default feature order function ol.renderer.vector.defaultOrder.
 * Uses numeric ids instead of string ids to avoid string parsing to integer each time features are compared.
 *
 * @param {module:ol/Feature~Feature} feature1 - Feature 1.
 * @param {module:ol/Feature~Feature} feature2 - Feature 2.
 * @return {number} Order.
 */
export function defaultRenderOrder (feature1, feature2) {
  return getNumericUid(feature1) - getNumericUid(feature2);
}

/**
 * Performance optimized extension of OL getUid function.
 * Creates numeric ids instead of string ids to avoid string parsing to integer afterwards. For example by sorting.
 * Gets a unique ID for an object. This mutates the object so that further calls
 * with the same object as a parameter returns the same value.
 * Unique IDs are still generated with OpenLayers function ol.getUid.
 *
 * @param {object} obj - The object to get the unique ID for.
 * @return {number} The unique ID for the object.
 */
export function getNumericUid (obj) {
  return obj.numericUid || (obj.numericUid = parseInt(getUid(obj)));
}

/**
 * Calculates the resolution for given scale, if scale is not provided then, default value is used
 * The Default value is 10000.
 *
 * @param {module:ol/View~View} view - view
 * @param {number} scale - Default value 10000
 * @return {number} resolution
 */
export function resolutionForScale (view, scale = DEFAULT_SCALE) {
  const units = view.getProjection().getUnits();
  const mpu = METERS_PER_UNIT[units];
  const resolution = scale / mpu / INCHES_PER_METER / DOTS_PER_INCH;
  return clamp(resolution, view.getMinResolution(), view.getMaxResolution());
}

/**
 * Calculates the scale for given resolution.
 *
 * @param {module:ol/View~View} view - view
 * @param {number} resolution - resolution
 * @return {number} scale
 */
export function scaleForResolution (view, resolution) {
  const units = view.getProjection().getUnits();
  const mpu = METERS_PER_UNIT[units];
  return resolution * mpu * INCHES_PER_METER * DOTS_PER_INCH;
}

/**
 * Calculates the aspect ratio for given extent.
 *
 * @param {WorldBoxDto}  extent - extent values (minX, minY, maxX, maxY)
 * @return {number} aspect ratio
 */
export function aspectRatioForMapExtent (extent) {
  const [ minX, minY, maxX, maxY ] = [ ...extent ];
  const width = maxX - minX;
  const height = maxY - minY;
  if (height >= 1) {
    return width / height;
  } else {
    return 0;
  }
}

/**
 * Calculates the width with aspect ratio of the height.
 *
 * @param {number} height - of the element
 * @param {number} aspectRatio - of the element
 * @return {number} width of the element
 */
export function calculateWidth (height, aspectRatio) {
  return aspectRatio * height;
}

/**
 * Calculates the height with aspect ratio of the width.
 *
 * @param {number} width - of the element
 * @param {number} aspectRatio - of the element
 * @return {number} height of the element
 */
export function calculateHeight (width, aspectRatio) {
  return width / aspectRatio;
}

export function getDpi (isHiDPI) {
  if (isHiDPI) {
    return DOTS_PER_INCH * window.devicePixelRatio;
  }
  return DOTS_PER_INCH;
}

/**
 * Calculates the view padding for the map to be used in the ol.View.
 * The toolbar and controls overlay the map. Setting a padding for the view ensures that an extent
 * we view.fit() is fully visible and is not hidden under overlaying elements.
 *
 * @param {ol.Map} map
 * @return {number[]} The array with view padding values in pixels. The order is top, right, bottom, left.
 */
export function calculateMapViewPadding (map) {
  // This function is called by the map view initialization, at that moment the toolbar and controls
  // are not initialized yet, so we cannot select elements and measure their actual size.
  const toolbarHeight = currentWindowMediaSmUp.matches ? 40 : 56; // toolbar height on phones or larger screens
  const controlsHeight = supportsTouch ? 38 : 31; // controls height on touch and not-touch devices
  const top = map.get(MapProperty.TOOLBAR_VISIBLE) ? toolbarHeight : 0;
  const bottom = map.get(MapProperty.CONTROLS_VISIBLE) ? controlsHeight : 0;
  return [ top, 0, bottom, 0 ];
}

/**
 * Adds the sidebar next to the map.
 * If '.map-and-sidebar' does not exist (for example in the workbook map), nothing happens.
 *
 * @param {HTMLElement|Function} sidebar - The sidebar element of its provider function.
 */
export function addSidebar (sidebar) {
  const sidebarContainer = getSidebarContainer();
  if (!sidebarContainer) {
    return;
  }
  const sidebarElement = (typeof sidebar === 'function') ? sidebar() : sidebar;
  sidebarContainer.append(sidebarElement);
}

/**
 * Finds OL Control in given map by control class
 *
 * @param map - OL map
 * @param controlClass - control class to find
 * @return {Control} - first map control of the given class
 */
export function getControl (map, controlClass) {
  return map.getControls().getArray().find(control => {
    return control instanceof controlClass;
  });
}

export function getSidebar (sidebarSelector) {
  return getSidebarContainer()?.querySelector(sidebarSelector);
}

function getSidebarContainer () {
  return document.querySelector('.map-and-sidebar');
}
